import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * trail-making-a - FTUE demo (3 essais progressifs pedagogiques).
 *
 * Pattern : 3 essais simples, pas de scoring engine.
 *   E1 : 3 cibles, decouverte.
 *   E2 : 5 cibles, prise de rythme.
 *   E3 : 8 cibles, proche du staircase reel.
 */
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
const DEMO_STEPS = [
    {
        idx: 1,
        titleKey: 'trail_making_a.demo.step1_title',
        hintKey: 'trail_making_a.demo.step1_hint',
        nTargets: 3,
    },
    {
        idx: 2,
        titleKey: 'trail_making_a.demo.step2_title',
        hintKey: 'trail_making_a.demo.step2_hint',
        nTargets: 5,
    },
    {
        idx: 3,
        titleKey: 'trail_making_a.demo.step3_title',
        hintKey: 'trail_making_a.demo.step3_hint',
        nTargets: 8,
    },
];
const STAGE_SIZE = 280;
const TARGET_SIZE = 36;
function placeDemoTargets(n, seed) {
    // Deterministic placement (seed) pour eviter re-renders chaotiques.
    let s = seed;
    const rand = () => {
        s = (s * 1664525 + 1013904223) % 4294967296;
        return s / 4294967296;
    };
    const out = [];
    const minDist = n <= 5 ? 0.18 : 0.13;
    for (let id = 1; id <= n; id += 1) {
        let placed = false;
        for (let a = 0; a < 200; a += 1) {
            const x = 0.08 + rand() * 0.84;
            const y = 0.08 + rand() * 0.84;
            let ok = true;
            for (const e of out) {
                const dx = e.x - x;
                const dy = e.y - y;
                if (Math.sqrt(dx * dx + dy * dy) < minDist) {
                    ok = false;
                    break;
                }
            }
            if (ok) {
                out.push({ id, x, y });
                placed = true;
                break;
            }
        }
        if (!placed)
            out.push({ id, x: rand(), y: rand() });
    }
    return out;
}
export function Demo({ onComplete, onSkip }) {
    const { t } = useTranslation(['games', 'common']);
    const [stepIdx, setStepIdx] = useState(0);
    const [nextExpected, setNextExpected] = useState(1);
    const [done, setDone] = useState(false);
    const step = DEMO_STEPS[stepIdx];
    const isLast = stepIdx === DEMO_STEPS.length - 1;
    const targets = useMemo(() => placeDemoTargets(step.nTargets, 17 + step.idx * 31), [step.idx, step.nTargets]);
    const handleTap = useCallback((id) => {
        if (done)
            return;
        if (id === nextExpected) {
            const newNext = id + 1;
            setNextExpected(newNext);
            if (newNext > step.nTargets) {
                setDone(true);
            }
        }
    }, [done, nextExpected, step.nTargets]);
    const handleNext = () => {
        if (isLast) {
            onComplete();
        }
        else {
            setStepIdx((i) => i + 1);
            setNextExpected(1);
            setDone(false);
        }
    };
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: t('common:eyebrow.demo_guided') }), _jsx("h2", { className: "font-display text-[22px] font-medium leading-7 tracking-cy-display text-cyan-text", children: t(step.titleKey) }), _jsx("p", { className: "max-w-[320px] text-[13.5px] text-cyan-text-secondary", children: t(step.hintKey) }), _jsx("div", { className: "relative border border-cyan-border bg-cyan-surface", style: { width: STAGE_SIZE, height: STAGE_SIZE }, children: targets.map((tg) => {
                    const visited = tg.id < nextExpected;
                    const isNext = tg.id === nextExpected && !done;
                    return (_jsx("button", { type: "button", onClick: () => handleTap(tg.id), "aria-label": t('trail_making_a.target_aria', { id: tg.id }), className: [
                            'absolute flex items-center justify-center font-display text-[14px] font-medium transition-colors',
                            visited
                                ? 'bg-cyan-core text-cyan-surface'
                                : isNext
                                    ? 'border border-cyan-core bg-cyan-surface text-cyan-core'
                                    : 'border border-cyan-border bg-cyan-surface text-cyan-text',
                        ].join(' '), style: {
                            width: TARGET_SIZE,
                            height: TARGET_SIZE,
                            left: tg.x * STAGE_SIZE - TARGET_SIZE / 2,
                            top: tg.y * STAGE_SIZE - TARGET_SIZE / 2,
                        }, children: tg.id }, `demo-${tg.id}`));
                }) }), _jsx("p", { className: "text-[12px] text-cyan-text-secondary", children: done
                    ? t('trail_making_a.demo.done_hint')
                    : t('trail_making_a.demo.tap_next', { id: nextExpected }) }), _jsxs("div", { className: "flex flex-col items-stretch gap-s3 self-stretch", children: [_jsx("button", { type: "button", onClick: handleNext, disabled: !done, className: [
                            'inline-flex h-12 w-full items-center justify-center font-display text-[14px] font-medium tracking-[-0.01em] transition active:scale-[0.985]',
                            done
                                ? 'bg-cyan-core text-cyan-surface hover:bg-cyan-network'
                                : 'cursor-not-allowed border border-cyan-border-faint bg-cyan-bg text-cyan-text-faint',
                        ].join(' '), children: isLast ? t('common:actions.start_session') : t('common:actions.next_trial') }), _jsx("button", { type: "button", onClick: onSkip, className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: t('common:actions.skip_demo') })] })] }));
}
//# sourceMappingURL=demo.js.map